import { useState } from 'react';
import {
  CombinedValueControlProps,
  ControlService,
  StyleDefinitions,
  CombinedControlTypes,
  ShadowStyleValue,
  StyleDefinition,
} from '../../types';
import {
  findSharedValue,
  getStyleValue,
} from '../../common/valueResolverService';
import { CombinedControlConverter } from '../converters';
import { isShadowStyleValue } from '../../common/valueResolverService/valueGuards';
import { StyleVariableValueValue } from '@wix/document-services-types';

// @ts-expect-error - TODO: ds-types-bump
const boxShadowInitialValue: ShadowStyleValue = {
  type: 'BoxShadow',
  blurRadius: {
    type: 'Length',
    value: 0,
    unit: 'px',
  },
  spreadRadius: {
    value: 0,
    unit: 'px',
    type: 'Length',
  },
  offsetX: {
    type: 'Length',
    value: 1,
    unit: 'px',
  },
  offsetY: {
    type: 'Length',
    value: 1,
    unit: 'px',
  },
};

type CombinedStyle = {
  masterStyleId: string;
  value: ShadowStyleValue;
};

export function useCombinedValueControls({
  styleIds,
  combinedControlTypes,
  service,
}: {
  styleIds: string[];
  service: ControlService;
  combinedControlTypes: CombinedControlTypes[];
}): Record<CombinedControlTypes, CombinedValueControlProps> {
  if (styleIds.length === 0) {
    throw new Error('No style ids provided');
  }

  const [combinedStyle, setCombinedStyle] = useState<CombinedStyle>(() =>
    getInitialCombinedStyle(service.styles, styleIds, service.styleDefinitions),
  );

  const handleCombinedValueChange = (updatedPart: Record<string, any>) => {
    const updateMasterStyle = combinedValueMerger(
      combinedStyle.value,
      // @ts-expect-error - TODO: ds-types-bump
      updatedPart,
    );
    setCombinedStyle({
      masterStyleId: combinedStyle.masterStyleId,
      value: updateMasterStyle,
    });

    const updatedStyles = styleIds.reduce((acc, styleId) => {
      if (!isShadowStyleValue(service.styles[styleId].value)) {
        return acc;
      }
      const resultingStyle =
        styleId === combinedStyle.masterStyleId
          ? updateMasterStyle // @ts-expect-error - TODO: ds-types-bump
          : combinedValueMerger(service.styles[styleId].value, updatedPart);

      acc[styleId] = resultingStyle;
      return acc;
    }, {} as Record<string, any>);
    service.setStyles(updatedStyles);
  };

  return combinedControlPropsBuilder(
    combinedControlTypes,
    service,
    styleIds,
    combinedStyle,
    handleCombinedValueChange,
  );
}

function combinedValueMerger(
  exisingPart: CombinedStyle['value'],
  updatedPart: Partial<CombinedStyle['value']>,
): CombinedStyle['value'] {
  // @ts-expect-error - TODO: ds-types-bump
  return { ...exisingPart, ...updatedPart };
}

function initialValueCreator(
  style: {
    value: StyleVariableValueValue;
  },
  styleDefinition: StyleDefinition,
): ShadowStyleValue {
  const actualValue = getStyleValue(style, styleDefinition);
  if (isShadowStyleValue(actualValue)) {
    return actualValue;
  }

  return boxShadowInitialValue;
}

function getInitialCombinedStyle(
  styles: ControlService['styles'],
  styleIds: string[],
  styleDefinitions: StyleDefinitions,
): CombinedStyle {
  const [styleId] = styleIds;
  return {
    masterStyleId: styleId,
    value: initialValueCreator(styles[styleId], styleDefinitions[styleId]),
  };
}

function combinedControlPropsBuilder(
  combinedControlTypes: CombinedControlTypes[],
  service: ControlService,
  styleIds: string[],
  combinedStyle: CombinedStyle,
  handleCombinedValueChange: (updatedPart: Record<string, any>) => void,
) {
  const controls = combinedControlTypes.reduce((acc, controlType) => {
    acc[controlType] = {
      getControl: service.getControl,
      combinedValue: combinedStyle.value,
      onCombinedValueChange: handleCombinedValueChange,
      mixMode: getMixMode(
        combinedStyle,
        controlType,
        styleIds,
        service.styles,
        service.styleDefinitions,
      ),
    };
    return acc;
  }, {} as Record<CombinedControlTypes, CombinedValueControlProps>);

  return controls;
}

function getMixMode(
  combinedStyle: CombinedStyle,
  combinedControlType: CombinedControlTypes,
  styleIds: string[],
  styles: ControlService['styles'],
  styleDefinitions: StyleDefinitions,
): boolean {
  if (styleIds.length === 1) {
    return false;
  }
  const converter = new CombinedControlConverter(combinedControlType);
  const sharedValue = findSharedValue(
    styleIds,
    (styleId) => {
      const stylesObject =
        styleId === combinedStyle.masterStyleId
          ? { value: combinedStyle.value }
          : styles[styleId];

      const combinedValue = getStyleValue(
        stylesObject,
        styleDefinitions[styleId],
      );
      if (!isShadowStyleValue(combinedValue)) {
        return undefined;
      }
      // @ts-expect-error - TODO: ds-types-bump
      return converter.importValue(combinedValue)?.value;
    },
    (current, shared) => current === shared,
  );
  return sharedValue !== undefined;
}
